import React, { useEffect, useMemo, useState } from 'react';
import { StatCard } from './ui/stat-card';
import { RecentActivity } from './recent-activity';
import { QuickAccess } from './quick-access';
import { JobsList } from './jobs-list';
import { Card } from './ui/card';
import { fetchDashboardKpis } from '@/lib/dashboard-api';
import { useUser } from '@/contexts/UserContext';
import { useProject } from '@/components/ProjectSelector';
import { useNavigate } from 'react-router-dom';
import { useExperience } from '@/hooks/useExperience';
import { ClientDashboard } from './ClientDashboard';
import { colors } from '@/design-system/colors';
import '@/design-system/typography.css';
import '@/design-system/forms.css';

function InternalDashboard() {
  const { companyId } = useUser();
  const { selectedProject, projects } = useProject();
  const navigate = useNavigate();
  const [activeTasksCount, setActiveTasksCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [budgetUtilized, setBudgetUtilized] = useState<number>(0);
  const [budgetTotal, setBudgetTotal] = useState<number>(0);
  const [overdueTasksCount, setOverdueTasksCount] = useState<number>(0);
  const [overduePOCount, setOverduePOCount] = useState<number>(0);
  const [activePOCount, setActivePOCount] = useState<number>(0);
  const [activeProjectsCount, setActiveProjectsCount] = useState<number>(0);

  const subtitle = useMemo(() => {
    if (!selectedProject || selectedProject === 'all') return 'All Projects';
    const projectName = projects.find(p => p.id === selectedProject)?.name;
    return projectName ? `For ${projectName}` : 'Selected Project';
  }, [selectedProject, projects]);

  const currency = useMemo(() => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }), []);

  // The KPIs are computed by the backend (GET /api/dashboard/kpis): it applies the caller's permissions and project access
  useEffect(() => {
    let cancelled = false;
    if (!companyId) return;
    setIsLoading(true);
    fetchDashboardKpis(selectedProject)
      .then((kpis) => {
        if (cancelled) return;
        setActiveTasksCount(kpis.tasks.active);
        setOverdueTasksCount(kpis.tasks.overdue);
        setActivePOCount(kpis.purchase_orders.active);
        setOverduePOCount(kpis.purchase_orders.overdue);
        setActiveProjectsCount(kpis.active_projects);
        setBudgetTotal(kpis.budget.total);
        setBudgetUtilized(kpis.budget.utilized);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Error fetching dashboard KPIs:', err);
        setActiveTasksCount(0);
        setOverdueTasksCount(0);
        setActivePOCount(0);
        setOverduePOCount(0);
        setActiveProjectsCount(0);
        setBudgetTotal(0);
        setBudgetUtilized(0);
      })
      .finally(() => { if (!cancelled) setIsLoading(false); });
    return () => { cancelled = true; };
  }, [companyId, selectedProject]);

  // Calculate if KPIs are not OK
  const budgetUtilizationPercent = budgetTotal > 0 ? (budgetUtilized / budgetTotal) * 100 : 0;
  const isPOsNotOK = overduePOCount > 0;
  const isToDosNotOK = overdueTasksCount > 0;
  const isBudgetNotOK = budgetUtilizationPercent > 70;

  return (
    <div className="p-6 space-y-6 tracking-tight">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Active Projects"
          value={isLoading ? '—' : activeProjectsCount}
          additionalInfo="Total active projects"
          onClick={() => navigate('/project')}
          backgroundColor={colors.dashboardTileHealthGood}
          tooltip="Total number of active projects"
        />
        <StatCard
          title="POs behind schedule"
          value={isLoading ? '—' : `${overduePOCount} / ${activePOCount}`}
          additionalInfo={subtitle}
          onClick={() => navigate('/cost-management/purchase-orders')}
          backgroundColor={isPOsNotOK ? colors.dashboardTileHealthBad : undefined}
          tooltip="POs behind schedule are those that have a due date which has passed and are not yet received or voided"
        />
        <StatCard
          title="Budget Utilization"
          value={isLoading || budgetTotal <= 0 ? '—' : `${Math.round(budgetUtilizationPercent)}%`}
          additionalInfo={`${currency.format(budgetUtilized)} / ${currency.format(budgetTotal)}`}
          onClick={() => navigate('/cost-management/purchase-orders')}
          backgroundColor={isBudgetNotOK ? colors.dashboardTileHealthBad : undefined}
          tooltip="Budget utilization is calculated as the sum of all purchase order amounts divided by the total project budget"
        />
        <StatCard
          title="To-Dos behind schedule"
          value={isLoading ? '—' : `${overdueTasksCount} / ${activeTasksCount}`}
          additionalInfo={subtitle}
          onClick={() => navigate('/project-management/todos')}
          backgroundColor={isToDosNotOK ? colors.dashboardTileHealthBad : undefined}
          tooltip="To-Dos behind schedule are those tasks that have a due date which has passed and are not yet completed"
        />
      </div>

      {/* Activity and Quick Access Row */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr,25%] gap-6">
        <RecentActivity />
        <QuickAccess />
      </div>

      {/* Jobs Section */}
      <Card className="p-6">
        <JobsList />
      </Card>
    </div>
  );
} 

/**
 * The Home. Internal people get the company dashboard (figures, activity, jobs). Everyone else gets a Home with no internal figure -- and InternalDashboard is not even
 * mounted for them, so none of its queries (budgets, purchase orders, projects of the company) runs.
 */
export function Dashboard() {
  const experience = useExperience();
  return experience === 'internal' ? <InternalDashboard /> : <ClientDashboard />;
}
